import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { Calendar } from '@ionic-native/calendar';
import { ClientServiceProvider } from '../../../clients/services/client-service/client-service';
import { ConfigServiceProvider } from '../../../core/services/config-service/config-service';
import { EventFormType } from '../../../../model/EventFormType';
import { EventStatus } from '../../../../model/EventStatus';
import { EventType } from '../../../../model/EventType';
import { FilterServiceProvider, FilterType } from '../../../core/services/filter-service/FilterServiceProvider';
import { finalize, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { IClientModel } from '../../../clients/interfaces/i-client-model.interface';
import { Injectable } from '@angular/core';
import { MarketPower } from '../../../../model/MarketPower';
import { PickupTime } from '../../../../model/PickupTime';
import { PlayerServiceProvider } from '../../../core/services/player-service/player-service';

@Injectable()
export class EventsServiceProvider {
  public events: BehaviorSubject<SellionsEvent[]>;
  public employeeId: BehaviorSubject<number>;
  public eventEmployeeIdFilter: BehaviorSubject<number>;
  public haveSubordinatesEvents: Boolean;
  private filter: any;
  private _subordinates: Subordinate[] = [];
  private lastSynchronizationTime: Date;
  private readonly synchronizeTimeDiff = 1000 * 60 * 30;

  constructor(
    private calendar: Calendar,
    private clientService: ClientServiceProvider,
    private configService: ConfigServiceProvider,
    private filterService: FilterServiceProvider,
    private http: HttpClient,
    private player: PlayerServiceProvider,
  ) {
    this.events = new BehaviorSubject([]);
    this.employeeId = new BehaviorSubject(null);
    this.eventEmployeeIdFilter = new BehaviorSubject(null);
  }

  public getEventsForClient(clientId: number = -1): Promise<Array<SellionsEvent>> {
    let url = this.configService.getUrl('clientEvents').replace('{clientId}', clientId.toString());
    return this.http
      .get<Array<SellionsEvent>>(url)
      .toPromise()
      .then((result) => result);
  }

  public synchronize() {
    let currentTime = Number(new Date());
    let lastUpdate = this.lastSynchronizationTime ? Number(this.lastSynchronizationTime) : 0;

    if (currentTime - lastUpdate > this.synchronizeTimeDiff) {
      this.http
        .post(this.configService.getUrl('synchronize'), {})
        .pipe(finalize(() => this.load()))
        .subscribe(() => (this.lastSynchronizationTime = new Date()));
    }
  }

  public load(): Promise<SellionsEvent[] | void> {
    this.filter = this.filterService.getFilter(FilterType.CALENDAR);
    return this.getEvents(this.getPayload())
      .map((response) => {
        let events: SellionsEvent[];
        if (this.filter.sapId) {
          events = this.filterBySapId(response, Number(this.filter.sapId));
        } else {
          events = response;
        }

        this.events.next(events.sort((a: SellionsEvent, b: SellionsEvent) => (moment(a.startDate).isAfter(b.startDate) ? 1 : -1)));
        this._subordinates = this.createSubordinatesList(this.events.value);
        this.haveSubordinatesEvents = this.haveSubordinateEvent(this.events.value);
        return this.events.value;
      })
      .toPromise()
      .catch((err) => {
        console.error(err);
      });
  }

  private getPayload() {
    let payload = {},
      userId;

    if (this.filter.sort) {
      payload['sort'] = this.filter.sort;
    }
    if (payload['sort'] === 'date') {
      payload['sort'] = 'startDate';
    }

    if (this.filter.order) {
      payload['dir'] = this.filter.order;
    }

    if (this.filter.manager > -1 && this.filter.user === -1) {
      userId = this.filter.manager;
    } else if (this.filter.user !== -1) {
      userId = this.filter.user;
    } else {
      userId = this.player.player.id;
    }

    if (!userId) {
      userId = this.player.player['id'];
    }

    if (this.filter.searchQuery) {
      payload['searchQuery'] = '%' + this.filter.searchQuery + '%';
    }

    if (this.filter.state) {
      payload['state'] = this.filter.state;
    }

    payload['userId'] = userId;

    if (this.filter.month) {
      payload['month'] = this.filter.month;
    }

    if (this.filter.date) {
      let date = new Date();
      if (this.filter.date === '>90') {
        date = new Date(date.getTime() - 1000 * 60 * 60 * 24 * 90);
        payload['dateBefore'] = date.toISOString();
      } else {
        date = new Date(date.getTime() - 1000 * 60 * 60 * 24 * this.filter.date);
        payload['dateAfter'] = date.toISOString();
      }
    }

    return payload;
  }

  private filterBySapId(events: SellionsEvent[], sapId: number): SellionsEvent[] {
    return events.filter((event: SellionsEvent) => this.checkSapId(event, sapId));
  }

  private checkSapId(event: SellionsEvent, sapId: number): boolean {
    const client = this.clientService.getUserClientsAsArray().find((client: IClientModel) => event.clientId === client.clientId);
    return client.params.sapId === sapId;
  }

  public getEvents(params?: any): Observable<SellionsEvent[]> {
    let url = this.configService.getUrl('eventsGet');
    return this.http.get<SellionsEvent[]>(url, { params });
  }

  public addEvent(event: SellionsEvent): Observable<any> {
    let url = this.configService.getUrl('eventsAdd');
    return this.http.post(url, event);
  }

  public getEventById(id: number): SellionsEvent {
    let event = this.events.value.find((event) => event.eventId === id);
    if (event) {
      return event;
    } else {
      this.getEvents(this.getPayload())
        .pipe(take(1))
        .subscribe((events) => {
          return events.find((event) => event.eventId === id);
        });
    }
  }

  public editEvent(event: SellionsEvent): Observable<any> {
    let url = this.configService.getUrl('eventsEdit').replace('{eventId}', event.eventId.toString());
    return this.http.put(url, event);
  }

  public deleteEvent(eventId: number): Observable<any> {
    let url = this.configService.getUrl('eventsEdit').replace('{eventId}', eventId.toString());
    return this.http.delete(url);
  }

  public exportEvent(event: SellionsEvent) {
    const client: IClientModel = this.clientService.getById(event.clientId);
    this.calendar.createEventInteractively(client.name, null, event.comment, event.startDate, event.endDate);
  }

  public subscribe(callback) {
    return this.events.subscribe(callback);
  }

  public setEmployeeIdFilter(employeeId: number) {
    this.eventEmployeeIdFilter.next(employeeId);
  }

  public getEmployeeIdFilter(): number {
    return this.eventEmployeeIdFilter.getValue();
  }

  public setEmployee(employeeId: number) {
    this.employeeId.next(employeeId);
  }

  private haveSubordinateEvent(events: SellionsEvent[]): Boolean {
    let hasSubordinates = false;
    for (let i = 0; i < events.length; i++) {
      if (events[i].userId != this.player.player.id) {
        hasSubordinates = true;
      }
    }
    return hasSubordinates;
  }

  private createSubordinatesList(events: SellionsEvent[]): Subordinate[] {
    const list = {};
    events.forEach((event) => {
      if (this.player.player.id !== event.userId) {
        list[event.userId] = { id: event.userId, fullName: event.userFullName };
      }
    });
    return Object.keys(list).map((id) => list[id]);
  }

  public get subordinates(): Subordinate[] {
    return this._subordinates;
  }
}

export interface SellionsEvent {
  clientId?: number;
  clientName?: string;
  clientNIP?: string;
  comment: string;
  endDate: Date;
  eventId?: number;
  questionnaireParams?: QuestionnaireParams;
  recipientId: number;
  startDate: Date;
  status: EventStatus;
  type: EventType;
  formType: EventFormType;
  userId: number;
  userFullName: string;
  kpiId?: number;
}

export interface QuestionnaireParams {
  pickupTime: PickupTime;
  marketPower: MarketPower;
  walletParticipation: number;
  eventComment: string;
  transportIssues: boolean;
  transportIssuesComment: string;
  technicalIssues: boolean;
  technicalIssuesComment: string;
}

export interface Subordinate {
  id: number;
  fullName: string;
}



// WEBPACK FOOTER //
// ./src/modules/calendar/services/events-service/events-service.ts